/**
 * SDP tweaks for talking, not music.
 *
 * The browser's default Opus offer is tuned for a video call on wifi: stereo,
 * ~32-40 kbps, and it keeps sending during silence. A scorer is holding a phone
 * at a ground on mobile data and is broadcasting the same audio once per
 * listener, so every one of those defaults is wrong here.
 *
 * Three changes, in order of how much they matter:
 *
 *   usedtx=1              Discontinuous transmission. During silence Opus drops
 *                         to roughly 2 kbps instead of a full stream. A push-to-
 *                         talk channel is mostly silence, so this is the single
 *                         biggest saving and it costs nothing.
 *   maxaveragebitrate     24 kbps mono is comfortably above the point where
 *                         speech stops improving. Halves the uplink per peer.
 *   stereo=0              A phone mic is one capsule. Sending two channels of
 *                         it is pure waste.
 *
 * useinbandfec=1 stays on: forward error correction is what makes a lossy
 * mobile link sound like speech rather than clicks.
 *
 * This is string surgery on a protocol, which is normally a bad idea — but the
 * bitrate half of it is the only part reachable through setParameters(), and
 * DTX is not exposed by any browser API at all. Everything here is written to
 * leave the SDP untouched when it does not find what it expects.
 */

/** Opus payload types in an audio m-line, in the order the offer lists them. */
export function opusPayloadTypes(sdp: string): string[] {
  const out: string[] = [];
  for (const line of sdp.split(/\r?\n/)) {
    const m = /^a=rtpmap:(\d+)\s+opus\/48000/i.exec(line);
    if (m?.[1]) out.push(m[1]);
  }
  return out;
}

export interface VoiceOpusOptions {
  /** Ceiling in bits per second. */
  bitrate?: number;
  /** Send almost nothing while nobody is speaking. */
  dtx?: boolean;
}

/**
 * Rewrite the Opus fmtp lines for speech.
 *
 * Existing keys are replaced rather than appended — a duplicate key in an fmtp
 * line is undefined behaviour and Chrome and Safari disagree about which one
 * wins. If there is no fmtp line for a payload type, one is inserted directly
 * after its rtpmap, which is where a parser expects to find it.
 */
export function tuneOpusForVoice(sdp: string, opts: VoiceOpusOptions = {}): string {
  const bitrate = opts.bitrate ?? 24000;
  const dtx = opts.dtx ?? true;

  const pts = opusPayloadTypes(sdp);
  if (pts.length === 0) return sdp;

  const wanted: Record<string, string> = {
    stereo: "0",
    "sprop-stereo": "0",
    maxaveragebitrate: String(bitrate),
    useinbandfec: "1",
    usedtx: dtx ? "1" : "0",
  };

  // Preserve the file's own line ending. Mixing \n into \r\n SDP breaks
  // parsers that are strict about it.
  const eol = sdp.includes("\r\n") ? "\r\n" : "\n";
  const lines = sdp.split(/\r?\n/);
  const out: string[] = [];

  for (const line of lines) {
    const fmtp = /^a=fmtp:(\d+)\s+(.*)$/.exec(line);
    if (fmtp && pts.includes(fmtp[1]!)) {
      out.push(`a=fmtp:${fmtp[1]} ${mergeParams(fmtp[2]!, wanted)}`);
      continue;
    }
    out.push(line);

    const rtpmap = /^a=rtpmap:(\d+)\s+opus\/48000/i.exec(line);
    if (rtpmap) {
      const pt = rtpmap[1]!;
      const hasFmtp = lines.some((l) => new RegExp(`^a=fmtp:${pt}\\s`).test(l));
      if (!hasFmtp) out.push(`a=fmtp:${pt} ${mergeParams("", wanted)}`);
    }
  }
  return out.join(eol);
}

/** Merge `key=value;…` parameters, replacing any key we care about. */
function mergeParams(existing: string, wanted: Record<string, string>): string {
  const params = new Map<string, string>();
  for (const part of existing.split(";")) {
    const s = part.trim();
    if (!s) continue;
    const eq = s.indexOf("=");
    if (eq === -1) params.set(s, "");
    else params.set(s.slice(0, eq).trim(), s.slice(eq + 1).trim());
  }
  for (const [k, v] of Object.entries(wanted)) params.set(k, v);

  return [...params].map(([k, v]) => (v === "" ? k : `${k}=${v}`)).join(";");
}
